import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { maybeAlertBudgetForExpense } from "@/lib/budgetAlerts";

export default function QuickEntryDialog({ onCreated, categories, triggerClassName, open: openProp, onOpenChange: onOpenChangeProp, hideTrigger = false }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (v) => {
    if (!isControlled) setInternalOpen(v);
    if (onOpenChangeProp) onOpenChangeProp(v);
  };
  const [type, setType] = useState("expense");
  const [category, setCategory] = useState("");
  const [categoryAutoSet, setCategoryAutoSet] = useState(false);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [merchant, setMerchant] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // {category, source}
  const suggestTimer = useRef(null);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");

  const filteredCategories = (categories || []).filter((c) => c.type === type);

  // Load accounts when dialog opens
  useEffect(() => {
    if (!open) return;
    api
      .get("/accounts")
      .then((r) => {
        const list = r.data || [];
        setAccounts(list);
        if (list.length > 0 && !list.find((a) => a.id === accountId)) {
          const def = list.find((a) => a.is_default) || list[0];
          setAccountId(def.id);
        }
      })
      .catch(() => setAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    // Reset category when type changes
    if (filteredCategories.length > 0 && !filteredCategories.find((c) => c.name === category)) {
      setCategory(filteredCategories[0].name);
      setCategoryAutoSet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, categories]);

  // Debounced merchant → category suggestion (expense only)
  useEffect(() => {
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (type !== "expense") {
      setSuggestion(null);
      return;
    }
    const text = merchant.trim();
    if (text.length < 2) {
      setSuggestion(null);
      return;
    }
    suggestTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/insights/suggest-category", {
          params: { merchant: text },
        });
        if (data?.category) {
          setSuggestion({ category: data.category, source: data.source });
          // Auto-apply when category hasn't been manually chosen by user
          if (
            categoryAutoSet ||
            !filteredCategories.find((c) => c.name === category) ||
            category === filteredCategories[0]?.name
          ) {
            const exists = filteredCategories.find((c) => c.name === data.category);
            if (exists) {
              setCategory(data.category);
              setCategoryAutoSet(true);
            }
          }
        } else {
          setSuggestion(null);
        }
      } catch {
        setSuggestion(null);
      }
    }, 300);
    return () => suggestTimer.current && clearTimeout(suggestTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant, type]);

  const reset = () => {
    setType("expense");
    setCategory("");
    setAmount("");
    setDate(todayISO());
    setMerchant("");
    setNote("");
    setSuggestion(null);
    setCategoryAutoSet(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) {
      toast.error("Selecciona una categoría");
      return;
    }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("El monto debe ser mayor a 0");
      return;
    }
    if (!accountId) {
      toast.error("Selecciona una cuenta");
      return;
    }
    setLoading(true);
    try {
      await api.post("/transactions", {
        type,
        category,
        amount: amt,
        date,
        note,
        merchant: merchant.trim(),
        account_id: accountId,
      });
      toast.success(type === "income" ? "Ingreso registrado" : "Gasto registrado");
      if (type === "expense") {
        maybeAlertBudgetForExpense({ category, date });
      }
      setOpen(false);
      reset();
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const exists = filteredCategories.find((c) => c.name === suggestion.category);
    if (exists) {
      setCategory(suggestion.category);
      setCategoryAutoSet(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            className={
              triggerClassName ||
              "bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md h-11 px-5 font-medium"
            }
            data-testid="open-quick-entry-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo movimiento
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-[#131C2E] max-w-md" data-testid="quick-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#F1F5F9]">
            Registrar movimiento
          </DialogTitle>
          <DialogDescription className="text-sm text-[#64748B]">
            Anota un gasto o ingreso eligiendo categoría, monto y fecha.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <Tabs value={type} onValueChange={setType}>
            <TabsList className="grid grid-cols-2 w-full bg-[#1B2638]">
              <TabsTrigger
                value="expense"
                className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white"
                data-testid="tab-expense"
              >
                Gasto
              </TabsTrigger>
              <TabsTrigger
                value="income"
                className="data-[state=active]:bg-[#0052FF] data-[state=active]:text-white"
                data-testid="tab-income"
              >
                Ingreso
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
              Comercio (opcional)
            </Label>
            <Input
              type="text"
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              placeholder="Ej. Starbucks, Uber, Walmart…"
              className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
              maxLength={80}
              data-testid="entry-merchant-input"
            />
            {suggestion?.category && type === "expense" && (
              <button
                type="button"
                onClick={applySuggestion}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#EEF4FF] hover:bg-[#DDE8FF] text-[#0040CC] text-xs font-medium px-3 py-1.5 transition-colors"
                data-testid="entry-suggestion-chip"
              >
                <Sparkles className="w-3 h-3" />
                Sugerido: {suggestion.category}
                {suggestion.source === "history" && (
                  <span className="text-[10px] text-[#0040CC]/70">· tu historial</span>
                )}
              </button>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
              Cuenta
            </Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger
                className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                data-testid="entry-account-select"
              >
                <SelectValue placeholder="Selecciona cuenta" />
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E] max-h-64">
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: a.color }}
                      />
                      {a.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
              Categoría
            </Label>
            <Select value={category} onValueChange={(v) => { setCategory(v); setCategoryAutoSet(false); }}>
              <SelectTrigger
                className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                data-testid="entry-category-select"
              >
                <SelectValue placeholder="Selecciona categoría" />
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E] max-h-64">
                {filteredCategories.map((c) => (
                  <SelectItem key={c.id} value={c.name}>
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: c.color }}
                      />
                      {c.name}
                      {c.group && c.group !== "Ingresos" && (
                        <span className="text-xs text-[#94A3B8]">· {c.group}</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Monto</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="mt-2 h-11 bg-[#131C2E] border-[#2A3447] font-numeric text-lg"
                required
                data-testid="entry-amount-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Fecha</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                required
                data-testid="entry-date-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
              Nota (opcional)
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Notas o referencia..."
              className="mt-2 bg-[#131C2E] border-[#2A3447] resize-none"
              rows={2}
              data-testid="entry-note-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#2A3447]"
              data-testid="entry-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#0052FF] hover:bg-[#0040CC] text-white"
              data-testid="entry-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
